import { useMemo } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { ArrowUp, ArrowDown, Trash2 } from 'lucide-react';

import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';
import { friendlyAuthError } from '@/lib/auth-errors';
import { sectionNumber } from '@/lib/utils';
import { SketchLabelEditor } from './SketchLabelEditor';

interface SketchListProps {
  projectId: Id<'projects'>;
}

/**
 * Reactive list of the project's sketches. Thumbnails on the left,
 * label + actions on the right. Reorder via up/down buttons (we
 * deliberately avoid HTML5 drag-drop — sketches reorder rarely and
 * a11y is cleaner with explicit buttons).
 */
export function SketchList({ projectId }: SketchListProps) {
  const { token } = useAuth();
  const sketches = useQuery(
    api.sketches.listForProject,
    token ? { token, projectId } : 'skip',
  );
  const reorder = useMutation(api.sketches.reorder);
  const remove = useMutation(api.sketches.remove);
  const { toast } = useToast();

  const ordered = useMemo(() => sketches ?? [], [sketches]);

  if (!token || sketches === undefined) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner label="Loading sketches…" />
      </div>
    );
  }

  if (ordered.length === 0) {
    return (
      <div className="rounded-sm border border-dashed border-ink/20 bg-paper-2/30 p-8 text-center">
        <p className="font-serif text-lg italic text-ink">No sketches yet.</p>
        <p className="mt-2 text-sm text-graphite">
          Upload your first lo-fi sketch using the panel above.
        </p>
      </div>
    );
  }

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= ordered.length) return;
    if (!token) return;
    const next = [...ordered];
    [next[index], next[target]] = [next[target], next[index]];
    try {
      await reorder({
        token,
        projectId,
        orderedIds: next.map((s) => s._id),
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not reorder',
        description: friendlyAuthError(err),
      });
    }
  };

  const onDelete = async (sketchId: Id<'sketches'>, label: string) => {
    if (!token) return;
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    try {
      await remove({ token, sketchId });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not delete',
        description: friendlyAuthError(err),
      });
    }
  };

  return (
    <ul className="grid gap-3">
      {ordered.map((s, index) => (
        <li
          key={s._id}
          className="flex flex-col gap-4 rounded-sm border border-ink/15 bg-paper p-3 sm:flex-row sm:items-center"
        >
          {/* Order index */}
          <span className="font-mono text-[11px] text-graphite tabular-nums w-8 shrink-0 text-center">
            {sectionNumber(index + 1)}
          </span>

          {/* Thumbnail */}
          <div className="h-24 w-32 shrink-0 overflow-hidden rounded-sm border border-ink/10 bg-paper-2">
            {s.url ? (
              <img
                src={s.url}
                alt={s.label}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-graphite text-xs italic">
                no preview
              </div>
            )}
          </div>

          {/* Label + filename */}
          <div className="flex-1 min-w-0">
            <SketchLabelEditor sketchId={s._id} label={s.label} />
            <p className="mt-1 truncate font-mono text-[11px] text-graphite">
              {s.filename} · {(s.bytes / 1024).toFixed(0)}KB
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => move(index, -1)}
              disabled={index === 0}
              aria-label="Move sketch up"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => move(index, 1)}
              disabled={index === ordered.length - 1}
              aria-label="Move sketch down"
            >
              <ArrowDown className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-vermilion hover:text-vermilion"
              onClick={() => onDelete(s._id, s.label)}
              aria-label="Delete sketch"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}
